<template>
  <div class="hitpoints">
    <div class="row hp container vertical">
      <div class="title">
        <h4>Hit Points</h4>
        <div class="hitdice">
          <span class="label">HD:</span>&nbsp;{{ hitdice }}
          <Counter uses="level" lr="reset" name="hd" hide-reset />
          <UseResource v-if="simple" resource="hd" :amount="1" :dice="hitdie + '+con'" label="Hit Die" class="hd-button"
            ><Icon :name="'estia:' + hitdie"
          /></UseResource>
        </div>
      </div>

      <div v-if="currentHP > 0" class="hpbar">
        <div class="bar" :style="{ width: percentHP }"></div>
        <div v-if="temporaryHP" class="bar temp" :style="{ width: percentTempHP }"></div>
        <span class="label">
          {{ currentHP }}<span v-if="temporaryHP"> + {{ temporaryHP }}</span> /
          {{ maximumHP }}
          <Icon v-if="maxHpMod" :name="maxHpMod > 0 ? 'fa6-solid:angles-up' : 'fa6-solid:angles-down'" />
        </span>
      </div>
      <div v-else class="deathsaves">
        <div>
          Successes:
          <Counter uses="3" name="deathsuccess" lr=""></Counter>
        </div>
        <div>
          Failures:
          <Counter uses="3" name="deathfail" lr=""></Counter>
        </div>
      </div>
      <div class="tools container">
        <div class="delta container">
          <button @click="damage(delta)"><Icon name="estia:minus" /></button>
          <input v-model.number="delta" type="text" inputmode="numeric" pattern="[0-9]*" @wheel.prevent="scrollDelta" />
          <button @click="heal(delta)"><Icon name="estia:plus" /></button>
        </div>
        <div class="temp">
          <span class="label">Temp:&nbsp;</span>
          <input
            v-model.number="temporaryHP"
            type="text"
            inputmode="numeric"
            pattern="[0-9]*"
            @wheel.prevent="scrollTemp"
          />
        </div>
        <div class="max">
          <span class="label">Max:&nbsp;</span>
          <input v-model.number="maxHpMod" type="text" inputmode="numeric" pattern="-?[0-9]*" />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import _ from 'lodash'
import { clamp } from '../util'
import { type CharacterSheetData, getClassHitdie, hitDice, totalLevel } from '../statblock/statblock'
import { joinDice, resolveDice } from '~/assets/dice/diceEval'

const props = defineProps<{
  stats: CharacterSheetData
}>()

const mods = useMods()

const baseHP = ref(0)
const hpDeficit = useResource(ref('curHP'))
const temporaryHP = useResource(ref('tempHP'))
const maxHpMod = useResource(ref('maxHPMod'))

const level = computed(() => totalLevel(props.stats.level))

const delta = ref(0)
watch(delta, (d) => {
  delta.value = clamp(d, 0, maximumHP.value + temporaryHP.value)
})

const percentHP = computed(() => 100 * (currentHP.value / maximumHP.value) + '%')

const percentTempHP = computed(() => {
  const missing = 100 * (1 - currentHP.value / maximumHP.value)
  return clamp(100 * (temporaryHP.value / maximumHP.value), 0, missing) + '%'
})

const maximumHP = computed(() => baseHP.value + maxHpMod.value)
const currentHP = computed(() => maximumHP.value - hpDeficit.value)

const simple = computed(() => props.stats.level instanceof Number || Object.keys(props.stats.level).length === 1)
const hpdice = computed(() => joinDice(`${hitdice.value}`, level.value * mods().con, props.stats.bonushp || ''))
const hitdice = computed(() => {
  if (props.stats.hitdice) return props.stats.hitdice

  if (_.isNumber(props.stats.level)) return `${level.value}${hitdie.value}p`

  return Object.entries(props.stats.level)
    .map(([cls, lvl], idx) => `${lvl}${getClassHitdie(cls)}${idx === 0 ? 'p' : ''}`)
    .join('+')
})

// TODO: better account for multiclassing. This reads from `class`, but `level` has better information
const hitdie = computed(() => props.stats.hitdie || getClassHitdie(props.stats.class) || hitDice[props.stats.size])

onMounted(() => {
  baseHP.value = resolveDice(hpdice.value, mods(), true)
})

onRest('lr', () => {
  hpDeficit.value = 0
  temporaryHP.value = 0
})

onUseResource(ref('hitpoints'), (_, amount) => {
  if (amount > 0) heal(amount)
  if (amount < 0) damage(-amount) // damage still expects a positive number
})

const scrollDelta = (ev: WheelEvent) => {
  const direction = Math.sign(-ev.deltaY) // up is negative deltaY, but should be positive for us
  delta.value = clamp(delta.value + direction, 0, 1000000)
}

const scrollTemp = (ev: WheelEvent) => {
  const direction = Math.sign(-ev.deltaY) // up is negative deltaY, but should be positive for us
  temporaryHP.value = clamp(temporaryHP.value + direction, 0, 1000000)
}

const heal = (amount: number) => {
  amount = Math.abs(amount)
  const temp = clamp(hpDeficit.value - amount, 0, maximumHP.value)
  hpDeficit.value = temp
}

const damage = (amount: number) => {
  let remaining = Math.abs(amount)
  if (temporaryHP.value) {
    const tempDelta = clamp(remaining, 0, temporaryHP.value)
    temporaryHP.value -= tempDelta
    remaining -= tempDelta
    if (remaining < 0) return
  }

  const hpDelta = clamp(remaining, 0, currentHP.value)
  hpDeficit.value += hpDelta
}
</script>

<style scoped lang="postcss">
.hitpoints {
  grid-area: hitpoints;
  display: flex;
  flex-direction: column;
  gap: 0.5em;

  .title {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: baseline;
    flex-wrap: wrap;
    margin-bottom: 0.25em;
    gap: 0.5em;

    h4 {
      margin-top: 0;
    }

    .hitdice {
      display: flex;
      flex: 1;
      flex-direction: row;
      justify-content: flex-end;
      align-items: center;
      gap: 0.25em;
      max-width: 350px;

      .label {
        white-space: pre;
        color: var(--text-highlight);
      }

      .hd-button {
        align-self: stretch;
        margin: 0;
        padding: .25em .5em;
        margin-top: 4px;
      }
    }
  }

  .deathsaves {
    display: flex;
    height: 1.5em;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 0.25em;
  }

  .hpbar {
    height: 1.5em;
    position: relative;
    text-align: center;
    border-radius: 5px;
    border: 1px solid var(--primary);
    margin-bottom: 0.25em;
    display: flex;
    flex-direction: row;
    justify-content: flex-start;
    overflow-x: hidden;

    .bar {
      height: 100%;
      background: var(--primary);
      transition: width 0.25s ease-out;

      &.temp {
        background: repeating-linear-gradient(
          -45deg,
          var(--primary) 0,
          var(--primary) 2px,
          transparent 2px,
          transparent 5px
        );
      }
    }

    .label {
      position: absolute;
      z-index: 10;
      font-family: var(--font-stack-title);
      color: var(--text-highlight);
      left: 50%;
      transform: translateX(-50%);
    }
  }

  .tools {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    gap: 1em;

    .delta {
      display: flex;
      flex-direction: row;
      gap: 2px;
    }

    .temp,
    .max,
    .hitdice {
      .label {
        color: var(--text-highlight);
      }
    }

    input {
      padding: 0;
      margin: 0;
      text-align: center;
      width: 3em;
      font-family: var(--font-stack-title);
      color: var(--text-highlight);
      background: var(--background);
      border: 1px solid var(--primary);
      border-bottom: 4px solid var(--primary);
      border-radius: 4px;

      @media (max-width: 600px) {
        max-width: 2em;
      }

      &:focus-visible {
        outline: none;
        box-shadow: inset 0 0 5px var(--primary);
      }
    }

    button {
      padding: 0;
      margin: 0;
      width: 2em;
      align-content: center;
    }
  }
}
</style>
